import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { ReviewMedia } from '@/hooks/useReviewMedia';
import VideoPlayer from './VideoPlayer';

export interface ReviewMediaLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  media?: ReviewMedia;
  onPrev: () => void;
  onNext: () => void;
  currentIndex: number;
  totalCount: number;
}

/**
 * Shared full-screen viewer for review media.
 * Extracted from ReviewMediaGallery so feed heroes and the full gallery
 * use one implementation (photo, VideoPlayer, prev/next, counter, close).
 */
const ReviewMediaLightbox = ({
  open,
  onOpenChange,
  media,
  onPrev,
  onNext,
  currentIndex,
  totalCount,
}: ReviewMediaLightboxProps) => {
  if (!media) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 bg-black/95 border-none"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex items-center justify-center min-h-[60vh]">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 text-white hover:bg-white/20 z-10"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Navigation */}
          {totalCount > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 text-white hover:bg-white/20"
                onClick={onPrev}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 text-white hover:bg-white/20"
                onClick={onNext}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          {/* Media content */}
          <div className="w-full h-full flex items-center justify-center p-8">
            {media.media_type === 'photo' ? (
              <img
                src={media.media_url}
                alt=""
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <VideoPlayer
                key={media.id}
                src={media.media_url}
                poster={media.thumbnail_url}
                className="max-w-full max-h-[80vh]"
                debug={{
                  reviewId: media.review_id,
                  mediaId: media.id,
                  mediaType: media.media_type,
                }}
              />
            )}
          </div>

          {/* Counter */}
          {totalCount > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {currentIndex + 1} / {totalCount}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewMediaLightbox;
